import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import type { PatientNote, NoteSeverity } from '../../../services/patientNotes';
import { NoteRow } from './NoteRow';
import { PinIcon } from './PinIcon';

type FilterKey = 'all' | NoteSeverity;

export interface NotesListActions {
    onEdit: (note: PatientNote) => void;
    onDelete: (noteId: string) => void;
    onPin: (noteId: string, pinned: boolean) => void;
    onArchive: (noteId: string) => void;
}

export interface ArchivedNotesListActions {
    onUnarchive: (noteId: string) => void;
    onDelete: (noteId: string) => void;
}

interface NotesListBaseProps {
    notes: PatientNote[];
    isLoading?: boolean;
    /** Tighter paddings + smaller type for narrow columns (e.g. the work queue). */
    compact?: boolean;
    /** Slot at the right of the filter bar (e.g. an "Add note" button). */
    headerRight?: React.ReactNode;
}

interface NotesListActiveProps extends NotesListBaseProps {
    archived?: false;
    /** Owner actions (pin / edit / archive / delete). Omit for a read-only list. */
    actions?: NotesListActions;
    /** CTA shown in the empty state (e.g. "Add the first note"). */
    emptyAction?: React.ReactNode;
}

interface NotesListArchivedProps extends NotesListBaseProps {
    archived: true;
    archivedActions: ArchivedNotesListActions;
}

type NotesListProps = NotesListActiveProps | NotesListArchivedProps;

const NOOP = (): void => {};

/**
 * Severity filter bar + rows, reused for the active and archived views.
 * Actions and CTAs are injected by NotesTab, which owns the mode switch.
 */
export function NotesList(props: NotesListProps) {
    const { notes, isLoading = false, compact = false, headerRight } = props;
    const isArchivedView = props.archived === true;
    const [filter, setFilter] = useState<FilterKey>('all');

    const headerPad = compact ? 'px-3 py-2' : 'px-5 py-3';
    const listPad = compact ? 'px-3 py-3' : 'px-5 py-4';
    const btnPad = compact ? 'px-2 py-1 text-[11px]' : 'px-3 py-1.5 text-[12px]';

    if (isLoading) {
        return (
            <div className={`flex flex-col gap-3 ${listPad}`}>
                {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-24 rounded-xl bg-gray-100 animate-pulse" />
                ))}
            </div>
        );
    }

    const filtered = filter === 'all' ? notes : notes.filter((n) => n.severity === filter);

    const counts = {
        all: notes.length,
        critical: notes.filter((n) => n.severity === 'critical').length,
        warning: notes.filter((n) => n.severity === 'warning').length,
        info: notes.filter((n) => n.severity === 'info').length
    };

    const filterTabs: Array<{ id: FilterKey; label: string }> = [
        { id: 'all', label: 'All' },
        { id: 'critical', label: 'Critical' },
        { id: 'warning', label: 'Warning' },
        { id: 'info', label: 'Info' }
    ];

    const emptyLabel = isArchivedView
        ? filter === 'all'
            ? 'No archived notes'
            : `No archived ${filter} notes`
        : filter === 'all'
          ? 'No notes yet'
          : `No ${filter} notes`;

    // Backend returns notes pre-sorted, we only partition. Pin grouping is
    // active-only - nothing is ever pinned in the archived view.
    const hasPinned = !isArchivedView && filter === 'all' && filtered.some((n) => n.pinned);
    const pinnedNotes = filtered.filter((n) => n.pinned);
    const unpinnedNotes = filtered.filter((n) => !n.pinned);

    const renderRow = (note: PatientNote) =>
        props.archived ? (
            <NoteRow
                key={note.id}
                note={note}
                compact={compact}
                view="archived"
                onUnarchive={props.archivedActions.onUnarchive}
                onDelete={props.archivedActions.onDelete}
            />
        ) : (
            <NoteRow
                key={note.id}
                note={note}
                compact={compact}
                canManage={Boolean(props.actions)}
                onEdit={props.actions?.onEdit ?? NOOP}
                onDelete={props.actions?.onDelete ?? NOOP}
                onPin={props.actions?.onPin ?? NOOP}
                onArchive={props.actions?.onArchive ?? NOOP}
            />
        );

    return (
        <div className="flex flex-col h-full">
            <div
                className={`flex items-center justify-between gap-2 ${headerPad} border-b border-gray-100 flex-shrink-0`}
            >
                <div className="flex items-center gap-1 flex-wrap">
                    {filterTabs.map((tab) => {
                        const active = filter === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setFilter(tab.id)}
                                className={`flex items-center gap-1.5 rounded-lg font-semibold font-inter transition-all ${btnPad} ${
                                    active
                                        ? 'bg-gray-900 text-white'
                                        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                                }`}
                            >
                                {tab.label}
                                <span
                                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                        active
                                            ? 'bg-white/20 text-white'
                                            : 'bg-gray-100 text-gray-400'
                                    }`}
                                >
                                    {counts[tab.id]}
                                </span>
                            </button>
                        );
                    })}
                </div>
                {headerRight}
            </div>

            <div className={`flex-1 overflow-auto ${listPad}`}>
                {filtered.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-40 gap-3 text-gray-400">
                        <InformationCircleIcon className="w-8 h-8 opacity-30" />
                        <span className="text-sm font-inter">{emptyLabel}</span>
                        {!props.archived && filter === 'all' && props.emptyAction}
                    </div>
                ) : (
                    <div className={`flex flex-col ${compact ? 'gap-2.5' : 'gap-3'}`}>
                        {hasPinned ? (
                            <>
                                <div className="text-[10px] uppercase tracking-wider font-semibold font-inter text-gray-400 mt-1 mb-1 flex items-center gap-1.5">
                                    <PinIcon className="w-3 h-3" />
                                    Pinned
                                </div>
                                {pinnedNotes.map(renderRow)}
                                <div className="text-[10px] uppercase tracking-wider font-semibold font-inter text-gray-400 mt-2 mb-1">
                                    All notes
                                </div>
                                {unpinnedNotes.map(renderRow)}
                            </>
                        ) : (
                            filtered.map(renderRow)
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
